import { Badge, Button, Checkbox, CloseButton, Group, Menu, Modal, Paper, Radio, SegmentedControl, Select, Skeleton, Stack, Switch, Table, Text, TextInput, Tooltip, VisuallyHidden, type MantineColor } from '@mantine/core';
import { hasLength, useForm } from '@mantine/form';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import type { ListLegalStandingResponse } from '@motionsafe/ms-console-api/admin/v1/legal_pb.js';
import { PeopleView, SeatState, StaffFilter as StaffFilterKind, type User } from '@motionsafe/ms-console-api/admin/v1/user_pb.js';
import { ConsoleRole, Feature, OperatorRole, Product, StaffRole, UserStatus } from '@motionsafe/ms-console-api/common/v1/common_pb.js';
import { IconArmchair, IconCheck, IconGavel, IconHourglass, IconKey, IconLogout, IconMail, IconMailForward, IconPlayerPause, IconPlayerPlay, IconSearch, IconShieldOff, IconTrash, IconUserCog, IconUserOff, IconUserPlus, IconUsers, IconUserSearch, IconUserShield } from '@tabler/icons-react';
import { useIsMutating, useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Fragment, useId, useState, type FormEvent } from 'react';

import { api } from '../../api/client';
import { errorText, legalStandingQuery, orgQuery, staffRolesQuery, useInvalidate, userCountsQuery, userQuery } from '../../api/hooks';
import { ActionsTrigger } from '../../components/actions';
import { confirmFirst } from '../../components/confirm';
import { useDialog, useShown } from '../../components/dialog';
import { useGridRows } from '../../components/grid';
import { GroupProvisioning } from '../../components/group-provisioning';
import { LegalCell, LegalStandingFacts, standingOf } from '../../components/legal-standing';
import { LOCKED, LOCKED_INPUT, LOCKED_ORDER, locked, LockedCaption, lockedNote, LockedSwitch, MenuLines, type MenuLine } from '../../components/locked';
import { DrawerSection, EmptyState, Facts, PageHeader, ToolbarActions, type StatProps } from '../../components/page';
import { useServerPages } from '../../components/pager';
import { usersIn } from '../../lib/counts';
import { PaneBody, PaneDrawer, PaneHeader } from '../../components/pane';
import { emailRule } from '../../lib/email';
import { fraction, poolText, when } from '../../lib/format';
import { ADMIN_CAPTION, isStaff, isStaffRole, joinRoles, sessionStaffRoles, STAFF_ORG_CAPTION, STAFF_ORG_SLUG, STAFF_ROLE_ORDER, staffRoleLabel, staffRoleName } from '../../lib/roles';
import { ICON_INLINE } from '../../theme';
import { Route as AppRoute, useAccess, useOrgId } from '../_app';
import { DialogLoadBar } from '../../components/top-load-bar';

/**
 * Which of MotionSafe's people the list shows, by what they hold: admins,
 * a staff role, or read only. It rides in the address, so the Staff roles
 * page links straight to the people in a role.
 */
export type StaffFilter = 'admin' | 'support' | 'fulfillment' | 'accounts' | 'read_only';
// `kind` is the service's name for the same filter.
const STAFF_FILTERS: { value: StaffFilter; kind: StaffFilterKind; label: string }[] = [
  { value: 'admin', kind: StaffFilterKind.ADMIN, label: 'Admins' },
  { value: 'support', kind: StaffFilterKind.SUPPORT, label: 'Support' },
  { value: 'fulfillment', kind: StaffFilterKind.FULFILLMENT, label: 'Fulfillment' },
  { value: 'accounts', kind: StaffFilterKind.ACCOUNTS, label: 'Accounts' },
  { value: 'read_only', kind: StaffFilterKind.READ_ONLY, label: 'Read only' },
];
const isStaffFilter = (v: unknown): v is StaffFilter => STAFF_FILTERS.some((f) => f.value === v);

export const Route = createFileRoute('/_app/people')({
  validateSearch: (s: Record<string, unknown>): { role?: StaffFilter | undefined } => (isStaffFilter(s['role']) ? { role: s['role'] } : {}),
  component: People,
});

// One list of an organization's people. A person has two separate things:
// whether they sign in to the console (an administrator), and whether they
// sign in on devices (an operator, who holds a seat). Someone can have
// either or both; neither is a kind of person. MotionSafe's own people are
// listed the same way, on the Staff page.

type Seat = 'seat' | 'waiting' | 'none';

/** What a person is, apart from who: the part a count (CountUsers) groups people by as well. */
type Standing = Pick<User, 'status' | 'consoleRole' | 'operatorAccess' | 'staffRoles' | 'mfaEnrolled' | 'seat'>;

const hasConsole = (u: Standing) => u.consoleRole !== ConsoleRole.OPERATOR;

/** Where a person stands with devices: holds a seat, waits for one, or has nothing to do with them. The service decides it (User.seat). */
const SEAT: Record<SeatState, Seat> = { [SeatState.UNSPECIFIED]: 'none', [SeatState.HOLDS]: 'seat', [SeatState.WAITING]: 'waiting', [SeatState.NONE]: 'none' };
const seatOf = (u: Standing): Seat => SEAT[u.seat];

/** The account's own state, apart from seats: the audit trail's words. */
function accountOf(u: User): { word: string; color: MantineColor } {
  if (u.status === UserStatus.DEPROVISIONED) return { word: 'deprovisioned', color: 'danger' };
  if (u.status === UserStatus.SUSPENDED) return { word: 'suspended', color: 'danger' };
  // Only someone the console invited is waiting on an invitation; a person from the identity provider or the directory has an account already and simply has not signed in.
  if (!u.lastLoginAt) return u.source === 'invite' ? { word: 'invited', color: 'warning' } : { word: 'not signed in yet', color: 'gray' };
  return { word: 'active', color: 'success' };
}

const roleLabel = (r: OperatorRole) => (r === OperatorRole.LAW_ENFORCEMENT ? 'Law enforcement' : 'Unrestricted');
const productLabel = (p: Product) => (p === Product.INSIGHTS ? 'Insights (collect)' : 'Privacy (wipe)');
const sourceLabel = (s: string) => (s === 'directory_sync' ? 'the directory' : s === 'self_enroll' ? 'self-enrollment' : s === 'idp' ? 'the identity provider' : 'invitation');
const AUTHORITY_HINT = 'Law enforcement requires a warrant number and an attestation before every collect, and limits parses to a declared scope.';
const ROLES = [
  { value: String(OperatorRole.UNRESTRICTED), label: 'Unrestricted' },
  { value: String(OperatorRole.LAW_ENFORCEMENT), label: 'Law enforcement' },
];

// A member of staff's role as the picker holds it: an admin, or staff with
// the staff roles ticked, where none is read only.
interface StaffChoice { admin: boolean; roles: StaffRole[] }
const choiceOf = (u: User): StaffChoice => ({ admin: u.consoleRole === ConsoleRole.MS_SUPERADMIN, roles: [...u.staffRoles] });
const sameChoice = (a: StaffChoice, b: StaffChoice) => a.admin === b.admin && (a.admin || (a.roles.length === b.roles.length && a.roles.every((r) => b.roles.includes(r))));

/** What staff who are not admins see on a member of staff's account: it is never theirs to change. */
const STAFF_ACCOUNT_CAPTION = 'Only a MotionSafe admin changes the account of a member of staff.';


const COLUMN_WIDTH: Record<string, number> = { Console: 110, Seat: 100, Authority: 150, '2FA': 100, Legal: 110 };

type View = 'operators' | 'administrators' | 'everyone';
const VIEW_OF: Record<View, PeopleView> = { operators: PeopleView.OPERATORS, administrators: PeopleView.ADMINISTRATORS, everyone: PeopleView.EVERYONE };

export function People() {
  const orgId = useOrgId();
  const { me } = AppRoute.useRouteContext();
  const org = useQuery(orgQuery(orgId));
  const staffOrg = org.data?.slug === STAFF_ORG_SLUG;
  // Inviting staff is an admin's; inviting an organization's people is Support's and Accounts' as well.
  const access = useAccess();
  const write = staffOrg ? access.unrestricted : access.may('InviteUser');
  const inviteNote = staffOrg ? ADMIN_CAPTION : access.caption('InviteUser');
  const [q, setQ] = useState('');
  const [debounced] = useDebouncedValue(q, 200);
  // Clearing applies at once; typing waits for a pause.
  const term = q.trim() ? debounced.trim() : '';
  const [view, setView] = useState<View>('operators');
  // On MotionSafe's own People, the role in the address narrows the list.
  const { role } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const setRole = (next: StaffFilter | undefined) => void navigate({ search: (prev) => ({ ...prev, role: next }), replace: true });
  const roleFilter = staffOrg ? STAFF_FILTERS.find((f) => f.value === role) : undefined;
  // The service counts the roster for the numbers, and lists a page of it, in the roster's order, for the table.
  const counts = useQuery(userCountsQuery(orgId)).data;
  const found = useServerPages({
    key: ['users', orgId],
    narrowed: `${orgId}|${term}|${staffOrg ? `role:${roleFilter?.value ?? ''}` : view}`,
    read: (page) => api.user.listUsers({ orgId, query: term, view: staffOrg ? PeopleView.EVERYONE : VIEW_OF[view], staffFilter: roleFilter?.kind ?? StaffFilterKind.UNSPECIFIED, ...page }),
    noun: 'people',
    enabled: org.data !== undefined,
  });
  // Where each person stands on MotionSafe's legal documents. Organization administrators read their own organization's.
  const legal = useQuery(legalStandingQuery(isStaff(me) ? orgId : '')).data;
  const legalShown = !!legal && legal.documents.length > 0;
  const [selected, setSelected] = useState<string | null>(null);
  const inviteDialog = useDialog();
  const inviteCaption = useId();

  // A different organization never borrows another's rows.
  const listed = found.query.data !== undefined && !(found.query.isPlaceholderData && found.query.data.users.some((u) => u.orgId !== orgId));
  const rows = listed ? found.query.data!.users : [];
  const gridRow = useGridRows(rows.map((u) => u.id));
  // As the page stands now, so the details follow a change made from them; read alone once they are on another.
  const onPage = selected ? rows.find((u) => u.id === selected) : undefined;
  const one = useQuery({ ...userQuery(selected), enabled: !!selected && !onPage });
  const row = selected ? onPage ?? one.data ?? null : null;
  const pane = useShown(row);

  const pool = org.data?.seatPool;
  const groups = counts ?? [];
  const standing = groups.filter((u) => u.status !== UserStatus.DEPROVISIONED);
  const admins = usersIn(standing, hasConsole);
  const active = usersIn(standing, (u) => u.status === UserStatus.ACTIVE);
  const enrolled = usersIn(standing, (u) => u.status === UserStatus.ACTIVE && u.mfaEnrolled);
  const full = !!pool && !pool.unlimited && pool.used >= pool.count;
  const stats: StatProps[] = staffOrg ? [
    { label: 'Staff', value: usersIn(standing), hint: 'Sign in to the console' },
    { label: 'Admins', value: usersIn(standing, (u) => u.consoleRole === ConsoleRole.MS_SUPERADMIN), hint: 'Can change anything; the rest change what their roles allow' },
    { label: 'Sign in on devices', value: usersIn(standing, (u) => seatOf(u) === 'seat'), hint: 'Staff who are operators as well' },
  ] : pool ? [
    { label: 'Seats in use', value: poolText(pool), hint: full ? 'No free seats' : `${pool.count - pool.used} free`, tone: full ? 'warn' : 'neutral' },
    { label: 'Waiting for a seat', value: pool.pending, hint: pool.pending === 0 ? 'Nobody is waiting' : full ? 'Cannot sign in on a device until a seat frees' : 'A seat is free; assign it from their menu', tone: pool.pending > 0 ? 'warn' : 'neutral' },
    { label: 'Administrators', value: admins, hint: 'Sign in to the console' },
    { label: '2FA set up', value: fraction(enrolled, active), hint: active === 0 ? 'Nobody active yet' : enrolled === active ? 'Everyone active' : `${active - enrolled} without 2FA yet`, tone: org.data?.policy?.requireMfa && enrolled < active ? 'warn' : 'neutral' },
  ] : [];

  const columns = ['Person', 'Console', 'Seat', 'Authority', '2FA', ...(legalShown ? ['Legal'] : [])];
  const inviteLabel = staffOrg ? 'Invite staff' : 'Invite person';
  const clear = () => { setQ(''); setView('everyone'); setRole(undefined); };

  return (
    <>
      <PageHeader
        title="People"
        description={staffOrg
          ? 'MotionSafe\'s own people. Everyone here signs in to the console; signing in on a device is given to each person who needs it.'
          : 'Everyone in this organization. An operator holds a seat and signs in on devices; an administrator signs in here. One person can be both.'}
        stats={org.isPending || !counts ? 'loading' : stats.length ? stats : undefined}
        statsCount={staffOrg ? 3 : 4}
      />

      <Group align="center" gap="sm" mb="md" wrap="wrap">
        <TextInput
          placeholder="Search name or email"
          aria-label="Search people"
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
          leftSection={<IconSearch {...ICON_INLINE} />}
          rightSection={q ? <CloseButton size="sm" aria-label="Clear search" onClick={() => setQ('')} /> : null}
          w={{ base: '100%', xs: 300 }}
        />
        {staffOrg && (
          <Select
            aria-label="Which staff to show"
            data={[{ value: 'everyone', label: 'Every role' }, ...STAFF_FILTERS.map((f) => ({ value: f.value, label: f.label }))]}
            value={roleFilter?.value ?? 'everyone'}
            allowDeselect={false}
            onChange={(v) => setRole(isStaffFilter(v) ? v : undefined)}
            w={{ base: '100%', xs: 180 }}
          />
        )}
        {!staffOrg && (
          <SegmentedControl
            aria-label="Which people to show"
            value={view}
            onChange={(v) => setView(v as View)}
            data={[{ value: 'operators', label: 'Operators' }, { value: 'administrators', label: 'Administrators' }, { value: 'everyone', label: 'Everyone' }]}
          />
        )}
        <ToolbarActions note={write ? undefined : inviteNote} noteId={inviteCaption}>
          {write
            ? <Button size="sm" leftSection={<IconUserPlus {...ICON_INLINE} />} onClick={inviteDialog.open}>{inviteLabel}</Button>
            : <Button size="sm" variant="default" leftSection={<IconUserPlus {...ICON_INLINE} />} {...locked(inviteCaption)}>{inviteLabel}</Button>}
        </ToolbarActions>
      </Group>

      <Paper p={0} style={{ overflow: 'hidden' }}>
        <Table.ScrollContainer minWidth={legalShown ? 1060 : 960}>
          <Table role="grid" layout="fixed" aria-label={staffOrg ? 'MotionSafe staff' : 'People in this organization'} aria-busy={!listed}>
            <Table.Thead>
              <Table.Tr>
                {/* Every column but the first has its own width, so nothing shifts from one page to the next. */}
                {columns.map((c) => <Table.Th key={c} {...(COLUMN_WIDTH[c] ? { w: COLUMN_WIDTH[c] } : {})}>{c}</Table.Th>)}
                <Table.Th w={115} ta="right" style={{ whiteSpace: 'nowrap' }}>Last sign-in</Table.Th>
                <Table.Th w={52}><VisuallyHidden>Actions</VisuallyHidden></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody style={{ opacity: found.query.isPlaceholderData ? 0.6 : 1, transition: 'opacity var(--motion-micro, 120ms) ease-out' }}>
              {!listed && !found.query.isError && [0, 1, 2, 3, 4].map((i) => (
                <Table.Tr key={i} bg="transparent" aria-hidden="true">
                  <Table.Td><Skeleton height={14} width="60%" mb={8} /><Skeleton height={12} width="80%" /></Table.Td>
                  {[56, 24, 96, 24, ...(legalShown ? [48] : [])].map((w, j) => <Table.Td key={j}><Skeleton height={12} width={w} /></Table.Td>)}
                  <Table.Td><Skeleton height={12} width={56} ml="auto" /></Table.Td>
                  <Table.Td />
                </Table.Tr>
              ))}
              {rows.map((u) => {
                const seat = seatOf(u);
                const account = accountOf(u);
                // The row is read left to right as a sentence about a person: who, then
                // what they have. The ordinary case is said in plain ink or a quiet
                // mark; color is kept for what needs someone's attention.
                const exception = account.word !== 'active';
                return (
                  <Table.Tr key={u.id} {...gridRow(u.id, () => setSelected(u.id))} aria-selected={selected === u.id} onClick={() => setSelected(u.id)} style={{ cursor: 'pointer', ...(u.status === UserStatus.DEPROVISIONED ? { opacity: 0.6 } : {}) }}>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap" align="center">
                        <Text size="sm" fw={500} truncate>{u.displayName}</Text>
                        {u.id === me.userId && <Text span size="sm" c="dimmed" style={{ flex: 'none' }}>(you)</Text>}
                        {exception && <Badge size="sm" color={account.color} style={{ flex: 'none' }}>{account.word}</Badge>}
                      </Group>
                      <Text size="xs" c="dimmed" truncate title={u.email}>{u.email}</Text>
                    </Table.Td>
                    <Table.Td><ConsoleCell user={u} /></Table.Td>
                    <Table.Td><SeatBadge seat={seat} /></Table.Td>
                    <Table.Td>
                      {/* The default authority recedes; the one that changes what a person may do is in full ink. */}
                      {seat === 'none' ? null : isStaffRole(u.consoleRole) || u.operatorRole !== OperatorRole.LAW_ENFORCEMENT
                        ? <Text size="sm" c="dimmed">Unrestricted</Text>
                        : <Text size="sm">Law enforcement</Text>}
                    </Table.Td>
                    <Table.Td>{u.mfaEnrolled ? <Mark label="2FA set up" /> : null}</Table.Td>
                    {legalShown && <Table.Td>{u.status === UserStatus.DEPROVISIONED ? null : <LegalCell data={legal} standing={standingOf(legal, u.id)} />}</Table.Td>}
                    <Table.Td ta="right" className="num" style={{ whiteSpace: 'nowrap' }}>
                      {u.lastLoginAt
                        ? <Tooltip label={when(u.lastLoginAt)}><Text span size="sm" c="dimmed">{when(u.lastLoginAt, { relative: true })}</Text></Tooltip>
                        : <Text span size="sm" c="dimmed">never</Text>}
                    </Table.Td>
                    {/* The menu is the row's own: a click or a key in it does not open the details. */}
                    <Table.Td ta="right" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                      <PersonActions user={u} self={u.id === me.userId} staffOrg={staffOrg} products={org.data?.products ?? []} full={full} compact />
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        {found.footer}
        {found.query.isError && (
          <Group gap="sm" justify="center" py="xl" px="lg">
            <Text size="sm" c="danger">Could not load people: {errorText(found.query.error)}.</Text>
            <Button variant="subtle" size="compact-sm" onClick={() => void found.query.refetch()} disabled={found.query.isFetching}>Try again</Button>
          </Group>
        )}
        {listed && rows.length === 0 && (term || roleFilter || (!staffOrg && view !== 'everyone' && usersIn(groups) > 0) ? (
          <EmptyState
            icon={IconUserSearch}
            title={term ? `Nobody matches “${term}”` : roleFilter ? (roleFilter.value === 'admin' ? 'No admins' : roleFilter.value === 'read_only' ? 'Nobody is read only' : `Nobody holds the ${roleFilter.label} role`) : view === 'operators' ? 'No operators yet' : 'No administrators yet'}
            body={term ? 'Search matches names and email addresses, among the people shown.' : roleFilter ? 'A MotionSafe admin gives roles from a person\'s menu.' : view === 'operators' ? 'Nobody holds a seat or waits for one. Invite someone with a seat, or assign one from a person\'s menu.' : 'Nobody signs in to the console for this organization yet.'}
            action={<Button variant="default" onClick={clear}>Show everyone</Button>}
          />
        ) : (
          <EmptyState icon={IconUsers} title="Nobody yet" body="Invite the first person from the button above the list." />
        ))}
      </Paper>

      {!staffOrg && org.data?.features.includes(Feature.DIRECTORY_SYNC) && <GroupProvisioning orgId={orgId} products={org.data.products} />}

      <PaneDrawer opened={pane.opened} onClose={() => setSelected(null)}>
        {pane.shown && <PersonPane key={pane.shown.id} user={pane.shown} self={pane.shown.id === me.userId} products={org.data?.products ?? []} full={full} staffOrg={staffOrg} legal={legal} />}
      </PaneDrawer>
      {write && inviteDialog.mounted && <InviteDialog round={inviteDialog.round} opened={inviteDialog.opened} onClose={inviteDialog.close} orgId={orgId} staffOrg={staffOrg} full={full} products={org.data?.products ?? []} />}
    </>
  );
}

// "Yes", as a mark and not a word: a check in the success ink, named on hover and for a screen reader.
function Mark({ label }: { label: string }) {
  return <span title={label} style={{ display: 'inline-flex' }}><IconCheck size={18} stroke={2} color="var(--mantine-color-success-text)" role="img" aria-label={label} /></span>;
}

// Whether they sign in to the console: what they are there, in plain words, or nothing.
function ConsoleCell({ user }: { user: User }) {
  if (!hasConsole(user)) return null;
  return <Text size="sm">{isStaffRole(user.consoleRole) ? staffRoleLabel(user.consoleRole, user.staffRoles) : 'Admin'}</Text>;
}

// Whether they hold a seat: a check, an hourglass for someone waiting for one, or nothing.
function SeatBadge({ seat }: { seat: Seat }) {
  if (seat === 'seat') return <Mark label="Holds a seat" />;
  // Waiting is a mark too, in the amber of something that wants attention; the words are on hover and for a screen reader.
  if (seat === 'waiting') return <span title="Waiting for a seat" style={{ display: 'inline-flex' }}><IconHourglass size={18} stroke={2} color="var(--mantine-color-warning-text)" role="img" aria-label="Waiting for a seat" /></span>;
  return null;
}

// Everything that can be done to a person, from one kebab: on their row and
// at the top of their details. The same two words for devices whoever they
// are: a seat is assigned, or unassigned. What a person cannot do to their
// own account is left out; someone else does it.
function PersonActions({ user, self, staffOrg, products, full, compact = false }: { user: User; self: boolean; staffOrg: boolean; /** The organization's products, offered with the seat. */ products: Product[]; /** No seat is free now. */ full: boolean; /** The row's smaller mark. */ compact?: boolean }) {
  const inv = useInvalidate();
  // Resolves once the roster and the seat counts have refetched, so a control never shows the old value in between.
  const done = (message: string) => { notifications.show({ message }); return inv('users', 'org'); };
  const fail = (e: unknown) => { notifications.show({ message: errorText(e), color: 'danger' }); };
  const name = user.displayName;
  const operatorOnly = user.consoleRole === ConsoleRole.OPERATOR;
  const staff = isStaffRole(user.consoleRole);
  const seat = seatOf(user);
  const gone = user.status === UserStatus.DEPROVISIONED;
  const suspended = user.status === UserStatus.SUSPENDED;
  // An invitation is for someone the console invited. Someone who came from the identity provider has an account there already; what they may lack is a password.
  const neverSignedIn = !user.lastLoginAt && !suspended && !gone && user.source === 'invite';
  // What the person signed in may do here, line by line. Staff who are not
  // admins take what their roles allow, and never change a member of staff's account.
  const access = useAccess();
  // MotionSafe's own people are never theirs to change (staff-org-guard.ts on the service).
  const guarded = (staff || staffOrg) && !access.unrestricted;
  const guardCaption = staff ? STAFF_ACCOUNT_CAPTION : STAFF_ORG_CAPTION;
  const line = (label: string, icon: MenuLine['icon'], method: string, onClick: () => void, danger = false): MenuLine => (
    { label, icon, danger, onClick: !guarded && access.may(method) ? onClick : undefined, caption: guarded ? guardCaption : access.caption(method) }
  );

  // A seat. For someone who is only an operator it is their place in the pool; for anyone else it is a flag on their account.
  const seatDialog = useDialog();
  const assign = useMutation({
    // What they get with the seat is set first, so the seat never arrives with more than was confirmed.
    mutationFn: async (given: { authority: OperatorRole; restricted: Product[] } | null) => {
      if (given && given.authority !== user.operatorRole) await api.user.setOperatorRole({ userId: user.id, operatorRole: given.authority });
      if (given && (given.restricted.length !== user.restrictedProducts.length || given.restricted.some((p) => !user.restrictedProducts.includes(p)))) await api.user.setRestrictedProducts({ userId: user.id, restrictedProducts: given.restricted });
      if (operatorOnly) return (await api.user.reinstateUser({ userId: user.id })).seatLimitReached;
      await api.user.setOperatorAccess({ userId: user.id, enabled: true });
      return false;
    },
    onSuccess: (stillWaiting) => { seatDialog.close(); return done(stillWaiting ? `No seat is free yet; ${name} is still waiting.` : staffOrg ? `${name} can sign in on devices assigned to MotionSafe.` : `${name} has a seat and can sign in on devices.`); },
    onError: fail,
  });
  const unassign = useMutation({
    mutationFn: async () => { if (operatorOnly) await api.user.revokeSeat({ userId: user.id }); else await api.user.setOperatorAccess({ userId: user.id, enabled: false }); },
    onSuccess: () => done(staffOrg ? `${name} no longer signs in on devices.` : `${name}'s seat is unassigned. They are signed out of every device.`),
    onError: fail,
  });
  const askUnassign = () => confirmFirst({
    title: staffOrg ? `Stop ${name} signing in on devices?` : `Unassign ${name}'s seat?`,
    body: `${staffOrg ? 'They' : 'Their seat frees for someone else. They'} are signed out of every device and cannot sign in on one until ${staffOrg ? 'it is given back' : 'they are assigned a seat again'}.${operatorOnly ? '' : ' Their place in the console is unchanged.'} Recorded in the audit trail under your name.`,
    label: staffOrg ? 'Stop device sign-in' : 'Unassign seat',
    onConfirm: () => unassign.mutateAsync(),
  });

  // The console. An organization's person is an administrator or is not; MotionSafe's are an admin or read only.
  const consoleAccess = useMutation({
    mutationFn: (enabled: boolean) => api.user.setConsoleAccess({ userId: user.id, enabled }),
    onSuccess: (_r, enabled) => done(enabled ? `${name} is an administrator and can sign in to the console.` : `${name} is no longer an administrator.`),
    onError: fail,
  });
  const askConsole = () => confirmFirst(operatorOnly ? {
    title: `Make ${name} an administrator?`,
    body: 'They can sign in to the console and do everything you can here, including inviting and removing people. Whether they hold a seat does not change. Recorded in the audit trail under your name.',
    label: 'Make administrator',
    onConfirm: () => consoleAccess.mutateAsync(true),
  } : {
    title: `Remove ${name}'s console access?`,
    body: 'They can no longer sign in to the console. They keep their seat and still sign in on devices. Recorded in the audit trail under your name.',
    label: 'Remove console access',
    onConfirm: () => consoleAccess.mutateAsync(false),
  });
  // A member of staff's role is chosen in a dialog: admin, or which staff roles.
  const roleDialog = useDialog();

  // The account.
  const emailDialog = useDialog();
  const revoke = useMutation({ mutationFn: () => api.user.revokeSessions({ userId: user.id, sessionId: '', note: 'from console' }), onSuccess: (r) => done(r.revoked === 0 ? 'They were not signed in on any device.' : `Signed out of ${r.revoked} device${r.revoked === 1 ? '' : 's'}.`), onError: fail });
  const askRevoke = () => confirmFirst({
    title: 'Sign them out of every device?',
    body: 'A device with a connection signs them out within 15 minutes; one that is offline does when it next connects, or at the end of its session. They can sign in again with their password and 2FA. Their account and seat are unchanged. Recorded in the audit trail under your name.',
    label: 'Sign out',
    onConfirm: () => revoke.mutateAsync(),
  });
  const resetMfa = useMutation({ mutationFn: () => api.user.resetMfa({ userId: user.id }), onSuccess: () => done('2FA removed. They set it up again at their next sign-in.'), onError: fail });
  // MotionSafe resetting a customer's person is told to their organization (UserService.ResetMfa).
  const { me } = AppRoute.useRouteContext();
  const told = isStaff(me) && !staff;
  const askReset = () => confirmFirst({
    title: 'Reset their 2FA?',
    body: `Their authenticator is removed, and every session ends. At the next sign-in they set up 2FA again.${told ? ' Their organization\'s administrators are told MotionSafe reset it.' : ''} Recorded in the audit trail under your name.`,
    label: 'Reset',
    onConfirm: () => resetMfa.mutateAsync(),
  });
  const resetPassword = useMutation({ mutationFn: () => api.user.resetPassword({ userId: user.id }), onSuccess: () => done(`A reset link is on its way to ${user.email}. Their password stays until they use it.`), onError: fail });
  const resendInvite = useMutation({ mutationFn: () => api.user.resendInvitation({ userId: user.id }), onSuccess: () => done(`A new invitation is on its way to ${user.email}. Earlier links stop working.`), onError: fail });
  const suspend = useMutation({ mutationFn: () => api.user.suspendUser({ userId: user.id, note: 'from console' }), onSuccess: () => done(`${name} suspended; they can no longer sign in.`), onError: fail });
  const askSuspend = () => confirmFirst({
    title: `Suspend ${name}?`,
    body: 'They can no longer sign in to the console or on a device, and a seat they hold frees. Nothing they set up changes. You can reinstate them later. Recorded in the audit trail under your name.',
    label: 'Suspend',
    danger: true,
    onConfirm: () => suspend.mutateAsync(),
  });
  const reinstate = useMutation({ mutationFn: () => api.user.reinstateUser({ userId: user.id }), onSuccess: () => done(`${name} reinstated.`), onError: fail });
  const remove = useMutation({
    mutationFn: () => api.user.deleteUser({ userId: user.id }),
    onSuccess: (r) => done(r.removed ? `${name} removed.` : `${name} can no longer sign in. Their name stays on the work they did.`),
    onError: fail,
  });
  const askDelete = () => confirmFirst({
    title: `Delete ${name}?`,
    body: user.lastLoginAt
      ? 'They can no longer sign in, their sessions end, and a seat they hold frees. Their account stays on record as deprovisioned, so the audit trail still names who did the work. Inviting the same email again brings them back. Recorded in the audit trail under your name.'
      : 'They have not signed in, so the account and its invitation are removed entirely. Recorded in the audit trail under your name.',
    label: 'Delete',
    danger: true,
    onConfirm: () => remove.mutateAsync(),
  });

  // On what authority they act on a device: for anyone who holds a seat or waits for one.
  const acts = seat !== 'none';
  const nextAuthority = user.operatorRole === OperatorRole.LAW_ENFORCEMENT ? OperatorRole.UNRESTRICTED : OperatorRole.LAW_ENFORCEMENT;
  const authorityItem = nextAuthority === OperatorRole.LAW_ENFORCEMENT ? 'Make law enforcement' : 'Make unrestricted';
  const setAuthority = useMutation({
    mutationFn: () => api.user.setOperatorRole({ userId: user.id, operatorRole: nextAuthority }),
    onSuccess: () => done(`${name} now acts as ${roleLabel(nextAuthority).toLowerCase()}. It applies at their next sign-in or refresh.`),
    onError: fail,
  });
  const askAuthority = () => confirmFirst({
    title: `Make ${name} ${roleLabel(nextAuthority).toLowerCase()}?`,
    body: nextAuthority === OperatorRole.LAW_ENFORCEMENT
      ? 'From their next sign-in, every collect requires a warrant number and an attestation, and parses are limited to a declared scope. Recorded in the audit trail under your name.'
      : 'From their next sign-in, collects no longer require a warrant number or an attestation, and parses are not scoped. Recorded in the audit trail under your name.',
    label: 'Change authority',
    onConfirm: () => setAuthority.mutateAsync(),
  });

  const label = `Actions for ${name}`;
  const seatItem = seat === 'seat' ? (staffOrg ? 'Stop device sign-in' : 'Unassign seat') : staffOrg ? 'Allow device sign-in' : 'Assign seat';
  if (gone) return null;
  // Devices, then the console: the two things a person can have.
  const having: MenuLine[] = [
    ...(!suspended ? [line(seatItem, IconArmchair, 'SetOperatorAccess', seat === 'seat' ? askUnassign : () => (staffOrg ? assign.mutate(null) : seatDialog.open()))] : []),
    ...(acts ? [line(authorityItem, IconGavel, 'SetOperatorRole', askAuthority)] : []),
    ...(staff && !self ? [line('Change role', IconUserCog, 'SetConsoleRole', roleDialog.open)] : []),
    ...(!staff && operatorOnly ? [line('Make administrator', IconUserShield, 'SetConsoleAccess', askConsole)] : []),
    // Console access is taken from someone who still has a seat; with neither they would have nothing, and are deleted instead.
    ...(!staff && !operatorOnly && !self && seat === 'seat' ? [line('Remove console access', IconUserOff, 'SetConsoleAccess', askConsole)] : []),
  ];
  const account: MenuLine[] = [
    ...(seat === 'seat' && user.lastLoginAt ? [line('Sign out of every device', IconLogout, 'RevokeSessions', askRevoke)] : []),
    ...(user.mfaEnrolled && !self ? [line('Reset 2FA', IconShieldOff, 'ResetMfa', askReset)] : []),
    // Someone who has signed in; an invitation is sent again to a new address by inviting it.
    ...(!self && user.lastLoginAt ? [line('Change email', IconMail, 'SetEmail', emailDialog.open)] : []),
    ...(!self ? [neverSignedIn ? line('Resend invitation', IconMailForward, 'ResendInvitation', () => resendInvite.mutate()) : line('Send password reset', IconKey, 'ResetPassword', () => resetPassword.mutate())] : []),
  ];
  const ending: MenuLine[] = self ? [] : [
    // Suspending is for people who sign in to the console; someone who is only an operator has their seat unassigned.
    ...(!operatorOnly ? [suspended ? line('Reinstate', IconPlayerPlay, 'ReinstateUser', () => reinstate.mutate()) : line('Suspend', IconPlayerPause, 'SuspendUser', askSuspend)] : []),
    line('Delete', IconTrash, 'DeleteUser', askDelete, true),
  ];
  const busy = assign.isPending || unassign.isPending || setAuthority.isPending || consoleAccess.isPending || suspend.isPending || reinstate.isPending || resetMfa.isPending || revoke.isPending;
  return (
    <>
      <Menu position="bottom-end">
        <Menu.Target>
          <ActionsTrigger label={label} loading={busy} {...(compact ? { size: 28 } : {})} />
        </Menu.Target>
        <Menu.Dropdown>
          {[having, account, ending].filter((g) => g.length > 0).map((g, i) => (
            <Fragment key={g[0]!.label}>
              {i > 0 && <Menu.Divider />}
              <MenuLines lines={g} />
            </Fragment>
          ))}
        </Menu.Dropdown>
      </Menu>
      {staff && roleDialog.mounted && <StaffRoleDialog opened={roleDialog.opened} round={roleDialog.round} user={user} onClose={roleDialog.close} />}
      {/* Each opening starts from what the person has now. */}
      {emailDialog.mounted && <SetEmailDialog opened={emailDialog.opened} round={emailDialog.round} user={user} onClose={emailDialog.close} />}
      {!staffOrg && seatDialog.mounted && <AssignSeatDialog opened={seatDialog.opened} round={seatDialog.round} user={user} products={products} full={full} pending={assign.isPending} onClose={seatDialog.close} onAssign={(given) => assign.mutate(given)} />}
    </>
  );
}

// A person's details: what they have, and for someone with a seat, on what
// authority they act and which products they may use.
function PersonPane({ user, self, products, full, staffOrg, legal }: { user: User; self: boolean; products: Product[]; full: boolean; staffOrg: boolean; /** Where everyone stands on the legal documents; absent while it loads. */ legal: ListLegalStandingResponse | undefined }) {
  const inv = useInvalidate();
  // Authority and products are an organization's administrators' to set, and among staff an admin's.
  const access = useAccess();
  const write = access.may('SetOperatorRole');
  const lockedWhy = access.caption('SetOperatorRole');
  const productsCaption = useId();
  const done = (message: string) => { notifications.show({ message }); return inv('users', 'org'); };
  const fail = (e: unknown) => { notifications.show({ message: errorText(e), color: 'danger' }); };
  const setRole = useMutation({ mutationFn: (operatorRole: OperatorRole) => api.user.setOperatorRole({ userId: user.id, operatorRole }), onSuccess: (_r, role) => done(`${user.displayName} now acts as ${roleLabel(role).toLowerCase()}. It applies at their next sign-in or refresh.`), onError: fail });
  const setRestricted = useMutation({ mutationFn: (restrictedProducts: Product[]) => api.user.setRestrictedProducts({ userId: user.id, restrictedProducts }), onSuccess: () => done('Product access updated. It applies at their next sign-in or refresh.'), onError: fail });
  const seat = seatOf(user);
  const account = accountOf(user);
  const staff = isStaffRole(user.consoleRole);
  // Optimistic while the call and the refetch after it are in flight.
  const roleNow = setRole.isPending ? setRole.variables : user.operatorRole;
  const roleShown = roleNow === OperatorRole.LAW_ENFORCEMENT ? OperatorRole.LAW_ENFORCEMENT : OperatorRole.UNRESTRICTED;
  const restrictedNow = setRestricted.isPending ? setRestricted.variables : user.restrictedProducts;
  const restricted = (p: Product) => restrictedNow.includes(p);
  // The consequence is named before anything changes; the select only moves once confirmed.
  const chooseRole = (role: OperatorRole) => {
    if (role === user.operatorRole) return;
    confirmFirst({
      title: `Make ${user.displayName} ${roleLabel(role).toLowerCase()}?`,
      body: role === OperatorRole.LAW_ENFORCEMENT
        ? 'From their next sign-in, every collect requires a warrant number and an attestation, and parses are limited to a declared scope. Recorded in the audit trail under your name.'
        : 'From their next sign-in, collects no longer require a warrant number or an attestation, and parses are not scoped. Recorded in the audit trail under your name.',
      label: 'Change authority',
      onConfirm: () => setRole.mutateAsync(role),
    });
  };
  return (
    <>
      <PaneHeader
        title={user.displayName}
        subtitle={`${user.email}${self ? ' (you)' : ''}`}
        state={<Badge color={account.color}>{account.word}</Badge>}
        actions={<PersonActions user={user} self={self} staffOrg={staffOrg} products={products} full={full} />}
      />
      <PaneBody>
        <DrawerSection title="Access">
          <Facts rows={[
            { label: 'Console', value: hasConsole(user) ? (staff ? staffRoleLabel(user.consoleRole, user.staffRoles) : 'Administrator') : 'no' },
            { label: 'Devices', value: seat === 'seat' ? (staffOrg ? 'signs in on devices' : 'holds a seat') : seat === 'waiting' ? 'waiting for a seat' : 'no' },
          ]} />
        </DrawerSection>
        {seat === 'seat' && (staff ? (
          <DrawerSection title="On devices">
            <Text size="sm">Unrestricted, with every product, on devices assigned to MotionSafe.</Text>
          </DrawerSection>
        ) : (
          <>
            <DrawerSection title="Authority">
              {write ? (
                <Select label="On what authority they act" description={AUTHORITY_HINT}
                  data={ROLES} value={String(roleShown)} allowDeselect={false} disabled={setRole.isPending}
                  onChange={(v) => { if (v !== null) chooseRole(Number(v) as OperatorRole); }} />
              ) : (
                <Select label="On what authority they act" description={lockedNote(AUTHORITY_HINT, lockedWhy)}
                  data={ROLES} value={String(roleShown)} allowDeselect={false} {...LOCKED_INPUT} inputWrapperOrder={LOCKED_ORDER} />
              )}
            </DrawerSection>
            <DrawerSection title="Products">
              <Text size="xs" c="dimmed">Everyone with a seat has the organization's products unless kept off one here.</Text>
              {products.length === 0 && <Text size="xs" c="dimmed">No products enabled yet; MotionSafe enables them.</Text>}
              <Stack gap="sm" mt={4}>
                {products.map((p) => (write
                  ? <Switch key={p} label={productLabel(p)} checked={!restricted(p)} disabled={setRestricted.isPending}
                    onChange={() => setRestricted.mutate(restricted(p) ? restrictedNow.filter((x) => x !== p) : [...restrictedNow, p])} />
                  : <LockedSwitch key={p} label={productLabel(p)} checked={!restricted(p)} aria-describedby={productsCaption} />
                ))}
              </Stack>
              {!write && products.length > 0 && <LockedCaption id={productsCaption}>{lockedWhy}</LockedCaption>}
            </DrawerSection>
          </>
        ))}
        {seat === 'waiting' && (
          <DrawerSection title="Authority">
            <Text size="sm">{roleLabel(user.operatorRole)}. It and their products can be changed once they have a seat.</Text>
          </DrawerSection>
        )}
        <DrawerSection title="Account">
          <Facts rows={[
            { label: '2FA', value: user.mfaEnrolled ? 'set up' : 'not set up' },
            { label: 'Added by', value: sourceLabel(user.source) },
            { label: 'Created', value: when(user.createdAt) },
            { label: 'Last sign-in', value: user.lastLoginAt ? when(user.lastLoginAt) : 'never' },
          ]} />
        </DrawerSection>
        {legal && user.status !== UserStatus.DEPROVISIONED && (
          <DrawerSection title="Legal documents">
            <LegalStandingFacts data={legal} standing={standingOf(legal, user.id)} />
          </DrawerSection>
        )}
      </PaneBody>
    </>
  );
}

// A seat comes with an authority and the organization's products, so both
// are confirmed with it: the person gets exactly what was looked at here.
// An administrator's change of someone's address. The service refuses it for
// someone who belongs to another organization too, unless MotionSafe makes
// it, and says so; the person confirms the new address with a code emailed
// to it at their next sign-in.
function SetEmailDialog({ opened, round, ...props }: { opened: boolean; round: number; user: User; onClose: () => void }) {
  return (
    <Modal opened={opened} onClose={props.onClose} title="Change email">
      <DialogLoadBar />
      <SetEmailForm key={round} {...props} />
    </Modal>
  );
}

function SetEmailForm({ user, onClose }: { user: User; onClose: () => void }) {
  const inv = useInvalidate();
  const form = useForm({ initialValues: { email: '' }, validate: { email: emailRule('Enter a complete email address.') } });
  const save = useMutation({
    mutationFn: ({ email }: typeof form.values) => api.user.setEmail({ userId: user.id, email: email.trim() }),
    onSuccess: (r) => {
      void inv('users', 'org');
      notifications.show({ message: `${user.displayName} now signs in with ${r.user?.email ?? form.values.email.trim()}, and confirms it with a code emailed there at their next sign-in.` });
      onClose();
    },
    onError: (e) => form.setFieldError('email', errorText(e)),
  });
  return (
    <form onSubmit={form.onSubmit((v) => { if (!save.isPending) save.mutate(v); })}>
      <Stack gap="lg">
        <Text size="sm" c="dimmed">{user.displayName} signs in with {user.email} now. At their next sign-in, on the console or a device, they enter a code emailed to the new address, so check it with them first. Their sessions, password and 2FA stay as they are. Recorded in the audit trail under your name.</Text>
        <TextInput label="New email" type="email" autoComplete="off" withAsterisk data-autofocus {...form.getInputProps('email')} />
        <Group className="dialog-footer" justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={save.isPending}>Change email</Button>
        </Group>
      </Stack>
    </form>
  );
}

type AssignSeatProps = { user: User; products: Product[]; full: boolean; pending: boolean; onClose: () => void; onAssign: (given: { authority: OperatorRole; restricted: Product[] }) => void };

function AssignSeatDialog({ opened, round, ...props }: AssignSeatProps & { opened: boolean; round: number }) {
  return (
    <Modal opened={opened} onClose={props.onClose} title={`Assign a seat to ${props.user.displayName}`}>
      <DialogLoadBar />
      <AssignSeatForm key={round} {...props} />
    </Modal>
  );
}

function AssignSeatForm({ user, products, full, pending, onClose, onAssign }: AssignSeatProps) {
  const [authority, setAuthority] = useState<OperatorRole>(user.operatorRole === OperatorRole.LAW_ENFORCEMENT ? OperatorRole.LAW_ENFORCEMENT : OperatorRole.UNRESTRICTED);
  const [restricted, setRestricted] = useState<Product[]>(user.restrictedProducts);
  const allowed = (p: Product) => !restricted.includes(p);
  const toggle = (p: Product) => setRestricted(allowed(p) ? [...restricted, p] : restricted.filter((x) => x !== p));
  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); if (!pending) onAssign({ authority, restricted }); }}>
      <Stack gap="lg">
        <Text size="sm" c="dimmed">{full ? 'No seat is free now, so they wait for one with what is set here.' : 'They can sign in on devices with what is set here.'} Recorded in the audit trail under your name.</Text>
        <Select label="Authority" description={AUTHORITY_HINT} data={ROLES} value={String(authority)} allowDeselect={false} onChange={(v) => { if (v !== null) setAuthority(Number(v) as OperatorRole); }} data-autofocus />
        <Stack gap="sm">
          <Text size="sm" fw={500}>Products</Text>
          {products.length === 0 && <Text size="sm" c="dimmed">No products enabled yet; MotionSafe enables them.</Text>}
          {products.map((p) => <Checkbox key={p} label={productLabel(p)} checked={allowed(p)} onChange={() => toggle(p)} />)}
        </Stack>
        <Group className="dialog-footer" justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={pending}>Assign seat</Button>
        </Group>
      </Stack>
    </form>
  );
}

const EMAIL_MISSING = 'Enter a complete email address to send the invitation.';
const NAME_MISSING = 'Enter their name to send the invitation.';

type InviteProps = { onClose: () => void; orgId: string; staffOrg: boolean; full: boolean; /** The organization's products, each given with the seat unless kept off here. */ products: Product[] };

// Inviting a person: who they are, then what they are given. The two things
// are asked separately because they are separate: a seat, the console, or both.
// The form is keyed by the Invite click that opened it, so it starts from the
// defaults of the organization in view, even when reopened while closing.
// It does not close while an invitation is being sent, so the answer lands in
// the form that sent it.
const INVITING = ['invite'];

function InviteDialog({ opened, round, ...props }: InviteProps & { opened: boolean; /** Which Invite click opened it. */ round: number }) {
  const sending = useIsMutating({ mutationKey: INVITING }) > 0;
  return (
    <Modal opened={opened} onClose={() => { if (!sending) props.onClose(); }} title={props.staffOrg ? 'Invite MotionSafe staff' : 'Invite a person'} size={440}>
      <DialogLoadBar />
      <InviteForm key={round} {...props} />
    </Modal>
  );
}

function InviteForm({ onClose, orgId, staffOrg, full, products }: InviteProps) {
  const inv = useInvalidate();
  // Signing in on devices is the default for an organization's people and
  // not for staff. Until it is ticked or cleared here it follows the
  // organization, which may still be loading when the form opens.
  const [seatChosen, setSeat] = useState<boolean | null>(null);
  const seat = seatChosen ?? !staffOrg;
  const [admin, setAdmin] = useState(false);
  const [authority, setAuthority] = useState<OperatorRole>(OperatorRole.UNRESTRICTED);
  const [restricted, setRestricted] = useState<Product[]>([]);
  // Support invites operators; an organization's administrators are Accounts' (InviteUser on the service).
  const { me } = AppRoute.useRouteContext();
  const mayInviteAdmin = me.consoleRole !== 'ms_staff' || sessionStaffRoles(me).includes(StaffRole.ACCOUNTS);
  // Staff are invited read only unless an admin or a staff role is asked for.
  const [staffChoice, setStaffChoice] = useState<StaffChoice>({ admin: false, roles: [] });
  const form = useForm({
    // Send invitation stays off until both fields pass, and a button that is
    // off never submits, so each field says what is wrong as the person leaves it.
    validateInputOnBlur: true,
    initialValues: { email: '', name: '' },
    validate: { email: emailRule(EMAIL_MISSING), name: hasLength({ min: 1 }, NAME_MISSING) },
  });
  const console_ = staffOrg || admin;
  const valid = form.isValid('email') && form.isValid('name') && (seat || console_);
  const invite = useMutation({
    mutationKey: INVITING,
    mutationFn: async ({ email, name }: typeof form.values) => {
      const consoleRole = staffOrg ? (staffChoice.admin ? ConsoleRole.MS_SUPERADMIN : ConsoleRole.MS_STAFF) : admin ? ConsoleRole.ORG_ADMIN : ConsoleRole.OPERATOR;
      // One call: someone with the console who signs in on devices as well takes their seat in it, and is invited without one when none is free.
      const r = await api.user.inviteUser({
        orgId,
        email,
        displayName: name.trim(),
        consoleRole,
        operatorRole: seat ? authority : 0,
        staffRoles: staffOrg && !staffChoice.admin ? staffChoice.roles : [],
        operatorAccess: console_ && seat,
        restrictedProducts: !staffOrg && seat ? restricted : [],
      });
      const seatRefused = r.seatLimitReached;
      return { seatRefused, sent: r.invitationSent, problem: r.invitationProblem };
    },
    onSuccess: ({ seatRefused, sent, problem }, { email }) => {
      void inv('users', 'org');
      // An invitation email the identity provider would not send is said
      // plainly, with its words, and what to do next.
      if (!sent) {
        notifications.show({ color: 'warning', autoClose: false, message: `${email} was added, but the invitation email was not sent${problem ? `: ${problem.replace(/\.$/, '')}` : ''}. Use Resend invitation from their menu, or ask MotionSafe if it keeps happening.` });
      } else {
        notifications.show(seatRefused && seat
          ? { color: 'warning', message: `${email} invited, but no seat was free${console_ ? '' : ': they are waiting for one'}.` }
          : { message: `${email} invited.` });
      }
      onClose();
    },
    // The server's sentence sits under the email, where the person looks.
    onError: (e) => form.setFieldError('email', errorText(e)),
  });
  return (
    <form noValidate onSubmit={form.onSubmit((values) => { if (valid && !invite.isPending) invite.mutate(values); })}>
      <Stack gap="lg">
        <TextInput label="Email" withAsterisk type="email" autoComplete="off" data-autofocus {...form.getInputProps('email')} />
        <TextInput label="Name" withAsterisk {...form.getInputProps('name')} />
        {staffOrg ? <StaffRoleField value={staffChoice} onChange={setStaffChoice} /> : null}
        <Stack gap="sm">
          <Text size="sm" fw={500}>What they are given{!staffOrg && <Text span c="var(--mantine-color-error)"> *</Text>}</Text>
          <Checkbox
            label={staffOrg ? 'Signs in on devices' : 'A seat'}
            description={staffOrg ? 'On devices assigned to MotionSafe, with every product.' : full ? 'Signs in on devices. No seat is free now, so they will wait for one.' : 'Signs in on devices.'}
            checked={seat}
            onChange={(e) => setSeat(e.currentTarget.checked)}
          />
          {seat && !staffOrg && (
            <Stack ml={32} gap="sm">
              <Select label="Authority" description="Law enforcement needs a warrant number and an attestation before every collect."
                data={ROLES} value={String(authority)} allowDeselect={false} onChange={(v) => { if (v !== null) setAuthority(Number(v) as OperatorRole); }} />
              {products.length > 0 && (
                <Stack gap={6}>
                  <Text size="sm" fw={500}>Products</Text>
                  {products.map((p) => <Checkbox key={p} label={productLabel(p)} checked={!restricted.includes(p)} onChange={(e) => setRestricted(e.currentTarget.checked ? restricted.filter((x) => x !== p) : [...restricted, p])} />)}
                </Stack>
              )}
            </Stack>
          )}
          {!staffOrg && (mayInviteAdmin
            ? <Checkbox label="The console" description="An administrator: signs in here and manages this organization." checked={admin} onChange={(e) => setAdmin(e.currentTarget.checked)} />
            : <Checkbox label="The console" description={lockedNote('An administrator: signs in here and manages this organization.', 'Inviting an administrator needs the Accounts staff role. A MotionSafe admin can give it to you.')} checked={false} onChange={() => undefined} mod={LOCKED} aria-disabled />)}
        </Stack>
        <Group className="dialog-footer" justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={invite.isPending}>Cancel</Button>
          <Button type="submit" disabled={!valid || invite.isPending}>Send invitation</Button>
        </Group>
      </Stack>
    </form>
  );
}

// What a member of staff may change: an admin changes anything; staff see
// everything and change what the roles they hold allow, and nothing with
// none. The roles' sentences are the service's own (ListStaffRoles).
function StaffRoleField({ value, onChange }: { value: StaffChoice; onChange: (next: StaffChoice) => void }) {
  const table = useQuery(staffRolesQuery).data;
  const summary = (r: StaffRole) => table?.roles.find((x) => x.role === r)?.summary ?? '';
  const toggle = (r: StaffRole, on: boolean) => onChange({ admin: false, roles: on ? STAFF_ROLE_ORDER.filter((x) => x === r || value.roles.includes(x)) : value.roles.filter((x) => x !== r) });
  return (
    <Radio.Group label="Role" value={value.admin ? 'admin' : 'staff'} onChange={(v) => onChange({ ...value, admin: v === 'admin' })}>
      <Stack gap="sm" mt="xs">
        <Radio value="staff" label="Staff" description="Sees every organization, and changes what the roles below allow. With none, read only." />
        {!value.admin && (
          <Stack ml={32} gap="sm">
            {STAFF_ROLE_ORDER.map((r) => <Checkbox key={r} label={staffRoleName(r)} description={summary(r)} checked={value.roles.includes(r)} onChange={(e) => toggle(r, e.currentTarget.checked)} />)}
          </Stack>
        )}
        <Radio value="admin" label="Admin" description="Changes anything for every organization, including keys, limits, and staff." />
      </Stack>
    </Radio.Group>
  );
}

function StaffRoleDialog({ opened, round, ...props }: { opened: boolean; round: number; user: User; onClose: () => void }) {
  return (
    <Modal opened={opened} onClose={props.onClose} title={`Change ${props.user.displayName}'s role`} size={440}>
      <DialogLoadBar />
      <StaffRoleForm key={round} {...props} />
    </Modal>
  );
}

function StaffRoleForm({ user, onClose }: { user: User; onClose: () => void }) {
  const inv = useInvalidate();
  const [choice, setChoice] = useState<StaffChoice>(() => choiceOf(user));
  const name = user.displayName;
  const save = useMutation({
    mutationFn: () => api.user.setConsoleRole({ userId: user.id, consoleRole: choice.admin ? ConsoleRole.MS_SUPERADMIN : ConsoleRole.MS_STAFF, staffRoles: choice.admin ? [] : choice.roles }),
    onSuccess: () => {
      void inv('users', 'org');
      notifications.show({ message: choice.admin ? `${name} is now a MotionSafe admin.` : choice.roles.length ? `${name} is now ${joinRoles(choice.roles)} staff.` : `${name} is now read-only staff.` });
      onClose();
    },
    onError: (e) => notifications.show({ message: errorText(e), color: 'danger' }),
  });
  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); if (!save.isPending) save.mutate(); }}>
      <Stack gap="lg">
        <Text size="sm" c="dimmed">It applies from their next action. Recorded in the audit trail under your name.</Text>
        <StaffRoleField value={choice} onChange={setChoice} />
        <Group className="dialog-footer" justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={save.isPending}>Cancel</Button>
          <Button type="submit" disabled={sameChoice(choice, choiceOf(user)) || save.isPending}>Change role</Button>
        </Group>
      </Stack>
    </form>
  );
}
